import type { NotificationData } from '../../../services/notification.service';
import type {
  ModalStateService,
  OpenNotificationModalOptions,
} from '../../../services/modal-state.service';
import type { ActiveSubscriptionGateService } from '../../../services/active-subscription-gate.service';
import type { LoggerService } from '../../../services/logger.service';
import {
  WayPurchaseModalStateService,
  OpenWayPurchaseModalOptions,
} from '../services/way-purchase-modal-state.service';
import { resolveLprNotificationTraceContext } from '../../../shared/utils/lpr-notification-trace.util';
import { resolveLprEntryIdFromNotificationData } from '../../../notifications/notification.utils';
import { isWayPurchaseNotification } from './way-purchase-notification.util';
import type { MultiEligibleSelectionModalStateService } from '../../multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { isMultiEligibleSelectionNotification } from '../../multi-eligible-selection-modal/utils/multi-eligible-confirm.util';

export {
  isWayPurchaseNotification,
  WAY_PURCHASE_NOTIFICATION_TYPES,
} from './way-purchase-notification.util';

/**
 * Open the Way purchase modal.
 * Stacking on an open NotificationModal is only allowed when
 * `preserveUnderlyingModal` is set (explicit {@link DIALOG_STACK_EXCEPTIONS.WAY_ON_NOTIFICATION}).
 * Otherwise `respect_state` is discarded while another top-level dialog holds the mutex.
 */
export function openWayPurchaseModal(
  wayPurchaseModalState: WayPurchaseModalStateService,
  _modalStateService: ModalStateService,
  options: OpenWayPurchaseModalOptions
): void {
  wayPurchaseModalState.openModal(options);
}

function logNotificationModalRoute(
  logger: LoggerService | undefined,
  route: string,
  notification: NotificationData,
  extra?: Record<string, any>
): void {
  if (!logger) {
    return;
  }
  logger.trackNotification(route, resolveLprNotificationTraceContext(notification, extra));
}

export function openRoutedNotificationModal(
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  options: OpenNotificationModalOptions,
  activeSubscriptionGate?: ActiveSubscriptionGateService,
  logger?: LoggerService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  if (activeSubscriptionGate?.tryConsumeWithoutModal(options.notification)) {
    logNotificationModalRoute(logger, 'routed_active_subscription_gate', options.notification, {
      skippedModal: true,
    });
    return;
  }

  if (isMultiEligibleSelectionNotification(options.notification) && multiEligibleModalState) {
    const entryId = resolveLprEntryIdFromNotificationData(
      options.notification.data as Record<string, unknown> | undefined
    );
    const openState = multiEligibleModalState.getCurrentState();
    if (openState.isOpen && entryId) {
      const openEntryId = resolveLprEntryIdFromNotificationData(
        openState.notification?.data as Record<string, unknown> | undefined
      );
      if (openEntryId && String(openEntryId) === String(entryId)) {
        logNotificationModalRoute(
          logger,
          'routed_multi_eligible_selection_modal_skipped',
          options.notification,
          { reason: 'already_open_for_entry' }
        );
        return;
      }
    }
    logNotificationModalRoute(
      logger,
      'routed_multi_eligible_selection_modal',
      options.notification
    );
    if (wayPurchaseModalState.getCurrentState().isOpen && options.behavior === 'replace') {
      wayPurchaseModalState.hideModal();
    }
    multiEligibleModalState.openModal({
      notification: options.notification,
      behavior: options.behavior,
      shouldStore: options.shouldStore,
    });
    return;
  }

  if (isWayPurchaseNotification(options.notification)) {
    logNotificationModalRoute(logger, 'routed_way_purchase_modal', options.notification);
    if (multiEligibleModalState?.getCurrentState().isOpen) {
      multiEligibleModalState.hideModal();
    }
    openWayPurchaseModal(wayPurchaseModalState, modalStateService, {
      notification: options.notification,
      behavior: options.behavior,
      shouldStore: options.shouldStore,
      clearDismissSuppression: options.clearDismissSuppression,
    });
    return;
  }

  logNotificationModalRoute(logger, 'routed_notification_modal', options.notification);
  if (multiEligibleModalState?.getCurrentState().isOpen) {
    if (options.behavior === 'replace') {
      multiEligibleModalState.hideModal();
    } else {
      modalStateService.deferNotificationUntilModalAvailable(
        options.notification,
        options.shouldStore
      );
      return;
    }
  }
  if (wayPurchaseModalState.getCurrentState().isOpen) {
    if (options.behavior === 'replace') {
      wayPurchaseModalState.hideModal();
    } else {
      modalStateService.deferNotificationUntilModalAvailable(
        options.notification,
        options.shouldStore
      );
      return;
    }
  }
  // Cancel any close-animation drain so queued Way notifications cannot reopen over the standard modal.
  wayPurchaseModalState.clearNotificationQueue();

  modalStateService.openNotificationModal(options);
}
